import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * AbsentsToday - Widget "Absents aujourd'hui" pour RH
 */
const AbsentsToday = ({ token }) => {
  const [absents, setAbsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAbsents = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard/absents-today`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        setAbsents(data.success ? data.data : []);
      } catch (err) {
        console.error('Erreur fetch absents:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAbsents();
  }, [token]);

  // Grouper par type d'absence
  const groupedByType = absents.reduce((acc, absent) => {
    const key = absent.absence_code || 'AUTRE';
    if (!acc[key]) {
      acc[key] = {
        name: absent.absence_name || 'Autre',
        color: absent.absence_color || '#6c757d',
        users: []
      };
    }
    acc[key].users.push(absent);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Absents aujourd'hui</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Absents aujourd'hui</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h4 style={styles.title}>
        Absents aujourd'hui
        {absents.length > 0 && (
          <span style={styles.count}>({absents.length})</span>
        )}
      </h4>

      {absents.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>🎉</span>
          <p style={styles.emptyText}>Tout le monde est présent</p>
        </div>
      ) : (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={styles.colName}>Nom</span>
            <span style={styles.colType}>Type</span>
            <span style={styles.colGroup}>Groupe</span>
          </div>
          {absents.slice(0, 8).map((absent, index) => (
            <div key={absent.id || index} style={styles.tableRow}>
              <span style={styles.colName}>{absent.name}</span>
              <span
                style={{
                  ...styles.typeBadge,
                  backgroundColor: absent.absence_color || '#6c757d'
                }}
              >
                {absent.absence_code}
              </span>
              <span style={styles.colGroup}>{absent.group_name || '—'}</span>
            </div>
          ))}
          {absents.length > 8 && (
            <div style={styles.moreRow}>
              +{absents.length - 8} autres absents
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  title: {
    margin: '0 0 16px 0',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  count: {
    backgroundColor: '#ffc107',
    color: '#000',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 100px',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '0.8rem',
    color: '#6c757d',
    fontWeight: '500'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 80px 100px',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #e9ecef',
    alignItems: 'center'
  },
  colName: {
    fontSize: '0.9rem',
    color: '#1a1a2e',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  colType: {
    fontSize: '0.8rem'
  },
  colGroup: {
    fontSize: '0.85rem',
    color: '#6c757d',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  typeBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'white',
    textAlign: 'center'
  },
  moreRow: {
    textAlign: 'center',
    padding: '10px',
    fontSize: '0.85rem',
    color: '#6c757d',
    fontStyle: 'italic'
  }
};

export default AbsentsToday;
